/**
 * Инициализация i18next + react-i18next (Фаза 4, итерация И1 + И3).
 *
 * Канонический паттерн `react-i18next`: `i18n.use(initReactI18next).init(...)`
 * регистрирует глобальный i18n-instance, благодаря чему хук `useTranslation()`
 * работает в компонентах **без** явного `<I18nextProvider>` (важно для
 * компонентных тестов — см. [`test/setup.ts`](../test/setup.ts)).
 *
 * Локаль определяется **один раз** из `window.location.hostname` (решение 2.2):
 * - `ru` (apex / localhost / неизвестный поддомен) — встроен в основной бандл
 *   синхронно (источник истины + fallback), НЕ грузится по сети → мгновенный
 *   старт на ru, как в И1.
 * - `en` / `cn` — подгружаются **лениво** через `i18next-resources-to-backend`,
 *   который оборачивает динамический `import('../locales/<lng>/common.json')` в
 *   backend-плагин i18next. Vite создаёт под каждый язык отдельный чанк с хэшем в
 *   имени (cache-busting «бесплатно» через nginx `/assets/`), а ru-большинство не
 *   тянет лишний вес. `partialBundledLanguages: true` разрешает смешивать встроенный
 *   `ru` и лениво грузимые `en`/`cn`. При ошибке/отсутствии ключа — fallback на `ru`.
 *
 * react-i18next по умолчанию использует Suspense: пока каталог `en`/`cn`
 * подгружается, компоненты с `useTranslation()` приостанавливаются — поэтому в
 * [`main.tsx`](../main.tsx) приложение обёрнуто в `<Suspense>`. На `ru` подгрузки
 * нет → подвешивания не происходит (поведение И1 сохранено).
 *
 * Импорт этого модуля в `main.tsx` даёт side-effect инициализацию до первого
 * рендера.
 */
import i18n from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';

import ruCommon from '../locales/ru/common.json';
import { DEFAULT_LOCALE, resolveInitialLocale, type Locale } from './locale';

/**
 * Локаль приложения на старте (0.14.2): явный выбор пользователя (localStorage,
 * ставится переключателем) → поддомен → `ru`. SSR/тесты без `window` безопасно
 * получают `ru` (resolveInitialLocale → detectLocale обрабатывает пустую строку).
 */
export const initialLocale: Locale = resolveInitialLocale(
  typeof window !== 'undefined' && typeof window.location !== 'undefined'
    ? window.location.hostname
    : '',
);

void i18n
  .use(initReactI18next)
  .use(
    // Lazy-loading каталогов en/cn: явные динамические import() на каждую локаль,
    // чтобы Vite создал под неё отдельный JSON-чанк (cache-busting через хэш имени
    // и nginx /assets/ immutable). `ru` сюда НЕ попадает — он встроен в основной
    // бандл статически (источник истины/фallback) и не должен матчиться динамическим
    // импортом (иначе Vite предупреждает о смешанном static/dynamic импорте ru).
    // Для ru/неизвестной локали/неймспейса возвращаем пустой ресурс — i18next всё
    // равно возьмёт ru из `resources` (partialBundledLanguages + fallbackLng).
    resourcesToBackend((locale: string, namespace: string) => {
      if (namespace !== 'common') return Promise.resolve({});
      switch (locale) {
        case 'en':
          return import('../locales/en/common.json');
        case 'cn':
          return import('../locales/cn/common.json');
        default:
          return Promise.resolve({});
      }
    }),
  )
  .init({
    // ru — единственный синхронно встроенный каталог (источник истины и fallback
    // для en/cn). Интерполяция React-safe: react-i18next экранирует значения в
    // JSX, поэтому escapeValue:false (стандарт для React).
    resources: {
      ru: { common: ruCommon },
    },
    lng: initialLocale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: ['ru', 'en', 'cn'],
    // Разрешает mixing: ru встроен в бандл, en/cn — подгружаются через backend.
    // Без этого флага i18next считал бы en/cn «незагруженными» для синхронного ru.
    partialBundledLanguages: true,
    defaultNS: 'common',
    ns: ['common'],
    interpolation: {
      escapeValue: false,
    },
  });

// <html lang> для a11y/SEO (решение 2.8): китайский → zh-CN, иначе сама локаль.
// На статике в index.html остаётся lang="ru" (apex-дефолт); здесь корректируем.
function applyHtmlLang(locale: Locale): void {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = locale === 'cn' ? 'zh-CN' : locale;
  }
}
// При старте: фиксируем локаль, вычисленную из поддомена.
applyHtmlLang(initialLocale);
// Подстраховка на случай динамической смены локали (i18n.changeLanguage без
// перезагрузки): синхронизируем <html lang>. Основной сценарий Фазы 4 — навигация
// на поддомен (полная перезагрузка, и init-вызова достаточно), но listener
// гарантирует корректность атрибута в любом случае.
i18n.on('languageChanged', (lng) => applyHtmlLang(lng as Locale));

export default i18n;
